// Phase 86AZ.59 — Public supplier landing page (/tedarikci)
// =========================================================
// Pitches the SummitFlow B2B supplier program: who we look for,
// what the journey looks like, our transparency promises, and where
// to sign up / sign in.
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight, ShieldCheck, Sparkles, ScanSearch, ChartLine,
  Truck, Lock, Eye, FileText, Award, Globe, Handshake,
  Building2, ArrowRight, CheckCircle2, Mail,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import { useLanguage } from '../contexts/LanguageContext';

const NAVY = '#0A3A5A';

// ── Shared section heading ────────────────────────────────────────
const SectionEyebrow = ({ children }) => (
  <span className="inline-block text-[11px] uppercase tracking-[0.28em] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 px-3 py-1 rounded-full">
    {children}
  </span>
);

// ── Benefit card ─────────────────────────────────────────────────
const BenefitCard = ({ icon: Icon, title, description, num }) => (
  <div className="group relative bg-white border border-[rgba(10,58,90,0.10)] rounded-2xl p-7 hover:border-emerald-300 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
    <span className="absolute top-5 right-5 text-[10px] font-mono tracking-[0.2em] text-emerald-600/60">
      {num}
    </span>
    <div className="w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-5 group-hover:bg-emerald-600 group-hover:border-emerald-600 transition-colors">
      <Icon className="w-5 h-5 text-emerald-700 group-hover:text-white transition-colors" />
    </div>
    <h3 className="font-playfair text-xl mb-2 leading-tight" style={{ color: NAVY }}>
      {title}
    </h3>
    <p className="text-[14px] text-[#4A5568] leading-relaxed">{description}</p>
  </div>
);

// ── Step strip item ───────────────────────────────────────────────
const StepItem = ({ num, title, copy, last }) => (
  <div className="relative flex flex-col items-start">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">
        {num}
      </div>
      {!last && (
        <div className="hidden md:block h-px w-12 lg:w-20 bg-gradient-to-r from-emerald-400 to-transparent" />
      )}
    </div>
    <h4 className="font-playfair text-lg leading-snug mb-1.5" style={{ color: NAVY }}>{title}</h4>
    <p className="text-[13.5px] text-[#4A5568] leading-relaxed max-w-xs">{copy}</p>
  </div>
);

// ── Transparency promise row ─────────────────────────────────────
const PromiseRow = ({ icon: Icon, title, copy }) => (
  <div className="flex items-start gap-4 py-5 border-b border-white/10 last:border-0">
    <div className="shrink-0 w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center">
      <Icon className="w-4 h-4 text-emerald-300" />
    </div>
    <div>
      <h4 className="font-semibold text-white mb-1">{title}</h4>
      <p className="text-[13.5px] text-white/70 leading-relaxed">{copy}</p>
    </div>
  </div>
);

export default function TedarikciLanding() {
  const { language } = useLanguage();
  const tr = language === 'tr';

  const benefits = tr ? [
    { icon: Globe,        t: 'Küresel Alıcı Erişimi',     d: 'SummitFlow kapsamında 195+ ülkeden gelen delegasyonlara, Mavi Bölge ve Yeşil Bölge kayıtlı kurumlara aynı çatı altında erişin.' },
    { icon: ShieldCheck,  t: 'Doğrulanmış B2B Pazar',     d: 'Her alıcı önceden onaylanmış kurumlardır. Spam talep yok; sadece somut, projeye bağlı siparişler alırsınız.' },
    { icon: ChartLine,    t: 'Şeffaf Fiyat & Marj',       d: 'Komisyon yapısı sabit ve açık ilan edilir. Sürpriz ücret, gizli kesinti yok — her satışta ne kazanacağınızı baştan görürsünüz.' },
    { icon: Truck,        t: 'Entegre Lojistik',          d: 'etkinlik alanına teslimat, kurulum slotları ve depolama planlaması platform üzerinden koordine edilir.' },
    { icon: Sparkles,     t: 'Dijital Vitrin',            d: 'Ürün varyantları, fiyat modeli (m²/gün/sabit), galeri ve teknik dokümanlarla profesyonel kataloğunuzu tek tıkla yayınlayın.' },
    { icon: Award,        t: 'Tedarikçi Skoru',           d: 'Zamanında teslim, kalite ve müşteri memnuniyetine göre artan skor; bir sonraki etkinlikte öncelikli görünürlük kazanın.' },
  ] : [
    { icon: Globe,        t: 'Global Buyer Access',       d: 'Reach delegations from 195+ countries plus all verified Blue Zone and Green Zone organizations of SummitFlow — under one roof.' },
    { icon: ShieldCheck,  t: 'Verified B2B Marketplace',  d: 'Every buyer is pre-approved. No spam RFQs — only concrete, project-bound orders reach you.' },
    { icon: ChartLine,    t: 'Transparent Pricing',       d: 'Commission structure is fixed and publicly disclosed. No surprise fees, no hidden cuts — you know what you earn upfront.' },
    { icon: Truck,        t: 'Integrated Logistics',      d: 'Delivery to the event venue, install slots and storage planning are coordinated directly through the platform.' },
    { icon: Sparkles,     t: 'Digital Storefront',        d: 'Publish a professional catalog in one click with variants, pricing models (per m²/day/flat), galleries and technical docs.' },
    { icon: Award,        t: 'Supplier Score',            d: 'A score that grows with on-time delivery, quality and buyer satisfaction — earn priority placement at the next event.' },
  ];

  const steps = tr ? [
    { t: 'Başvuru',       c: 'Şirket bilgileri, vergi numarası ve banka bilgileri ile 5 dakikada kayıt formunu doldurun.' },
    { t: 'Doğrulama',     c: 'Belgeleriniz uzman ekibimiz tarafından 48 saat içinde incelenir; gerekirse ek bilgi talep edilir.' },
    { t: 'Kataloğu Aç',   c: 'Onayın ardından ürün/hizmetlerinizi fotoğraf, varyant ve fiyat modeli ile yayına alın.' },
    { t: 'Satışa Başla',  c: 'Alıcılar sipariş verir, ödeme platform üzerinden tahsil edilir, gönderi planı otomatik oluşturulur.' },
  ] : [
    { t: 'Apply',         c: 'Submit your company info, tax ID and bank details — the form takes 5 minutes.' },
    { t: 'Verification',  c: 'Our specialists review your documents within 48h and may request additional info.' },
    { t: 'Open Catalog',  c: 'Once approved, publish your products/services with images, variants and pricing models.' },
    { t: 'Start Selling', c: 'Buyers place orders, payments are collected via the platform, and dispatch plans are generated automatically.' },
  ];

  return (
    <div className="bg-[#F7F5EF] min-h-screen">
      <Navbar />

      {/* ── HERO ─────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{
          background: `radial-gradient(60% 80% at 75% 20%, rgba(16,185,129,0.10) 0%, transparent 60%), radial-gradient(40% 50% at 0% 100%, rgba(10,58,90,0.06) 0%, transparent 60%)`
        }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-20 pb-28">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <SectionEyebrow>
                {tr ? 'Tedarikçi Programı · 14-16 Ekim 2026' : 'Supplier Program · 14-16 October 2026'}
              </SectionEyebrow>
              <h1 className="font-playfair text-5xl md:text-6xl lg:text-7xl leading-[0.95] mt-6 mb-7"
                  style={{ color: NAVY }}>
                {tr ? (
                  <>
                    Zirvenin<br/>
                    <span className="italic text-emerald-700">resmi tedarik</span> ağına katılın.
                  </>
                ) : (
                  <>
                    Join the official<br/>
                    <span className="italic text-emerald-700">supply network</span> of the summit.
                  </>
                )}
              </h1>
              <p className="text-[16px] md:text-[17px] text-[#3A4A5C] leading-relaxed max-w-xl mb-10">
                {tr
                  ? 'SummitFlow kapsamında stand donanımı, ekipman kiralama, ikram, taşıma ve teknik hizmet veren firmaları platformumuza davet ediyoruz. Doğrulanmış alıcılar, şeffaf komisyon ve dijital katalog ile sürdürülebilir bir ticaret deneyimi.'
                  : 'We invite companies providing stand fittings, equipment rental, catering, transport and technical services for SummitFlow. Verified buyers, transparent commission and a digital catalog — a sustainable trade experience.'}
              </p>

              <div className="flex flex-wrap items-center gap-3">
                <Link to="/tedarikci-kayit" data-testid="supplier-landing-signup"
                  className="group inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-7 py-4 rounded-full font-semibold shadow-lg shadow-emerald-600/20 transition-all">
                  {tr ? 'Tedarikçi Olmak için Başvur' : 'Apply to Become a Supplier'}
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Link>
                <Link to="/giris?tab=supplier&only=1" data-testid="supplier-landing-signin"
                  className="inline-flex items-center gap-2 border border-[rgba(10,58,90,0.18)] hover:border-emerald-500 hover:text-emerald-700 text-[#0A3A5A] px-7 py-4 rounded-full font-semibold transition-colors bg-white/60 backdrop-blur-sm">
                  {tr ? 'Hesabıma Giriş Yap' : 'Sign In to My Account'}
                </Link>
              </div>

              <div className="flex flex-wrap items-center gap-x-7 gap-y-2 mt-9 text-[12.5px] text-[#5A6B7D]">
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />{tr ? 'Üyelik ücretsiz' : 'Free to join'}</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />{tr ? '48 saatte değerlendirme' : '48-hour review'}</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />{tr ? 'Sabit komisyon' : 'Fixed commission'}</span>
              </div>
            </div>

            {/* Right column — stat card */}
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-3xl rotate-3 opacity-20" />
                <div className="relative bg-white border border-[rgba(10,58,90,0.10)] rounded-3xl p-8 shadow-xl">
                  <div className="flex items-center justify-between mb-6">
                    <SectionEyebrow>{tr ? 'Bir Bakışta' : 'At a Glance'}</SectionEyebrow>
                    <Building2 className="w-5 h-5 text-emerald-600" />
                  </div>
                  <dl className="space-y-5">
                    {[
                      { k: tr ? 'Beklenen Alıcı Kurum' : 'Expected Buyer Orgs', v: '4,500+' },
                      { k: tr ? 'Ülke Delegasyonu'     : 'Country Delegations', v: '195' },
                      { k: tr ? 'Etkinlik Süresi'      : 'Event Duration',      v: tr ? '3 gün' : '3 days' },
                      { k: tr ? 'Komisyon Oranı'       : 'Commission Rate',     v: '8.5%' },
                    ].map((row, i) => (
                      <div key={i} className="flex items-baseline justify-between gap-4 py-2 border-b border-[rgba(10,58,90,0.08)] last:border-0">
                        <dt className="text-[13px] text-[#5A6B7D]">{row.k}</dt>
                        <dd className="font-playfair text-2xl font-semibold" style={{ color: NAVY }}>{row.v}</dd>
                      </div>
                    ))}
                  </dl>
                  <div className="mt-7 pt-6 border-t border-[rgba(10,58,90,0.08)]">
                    <p className="text-[12px] text-[#5A6B7D] leading-relaxed">
                      {tr ? 'Veriler, SummitFlow organizasyon komitesi tahminlerine ve önceki dönem ortalamalarına dayanmaktadır.'
                          : 'Figures are based on the SummitFlow organizing committee estimates and historical averages.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── BENEFITS ─────────────────────────────────────────────── */}
      <section className="bg-white border-y border-[rgba(10,58,90,0.08)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="max-w-2xl mb-14">
            <SectionEyebrow>{tr ? 'Neden Katılmalı?' : 'Why Join?'}</SectionEyebrow>
            <h2 className="font-playfair text-4xl md:text-5xl leading-[1.05] mt-5" style={{ color: NAVY }}>
              {tr ? 'Sıradan bir tedarik portföyü değil — küresel bir buluşma platformu.'
                  : 'Not just another procurement portal — a global meeting platform.'}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => (
              <BenefitCard key={i} num={`0${i+1}`} icon={b.icon} title={b.t} description={b.d} />
            ))}
          </div>
        </div>
      </section>

      {/* ── PROCESS ──────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <div className="max-w-2xl mb-14">
          <SectionEyebrow>{tr ? 'Süreç Nasıl İşliyor?' : 'How It Works'}</SectionEyebrow>
          <h2 className="font-playfair text-4xl md:text-5xl leading-[1.05] mt-5" style={{ color: NAVY }}>
            {tr ? 'Dört basit adımda yayında.' : 'Live in four simple steps.'}
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
          {steps.map((s, i) => (
            <StepItem key={i} num={i + 1} title={s.t} copy={s.c} last={i === steps.length - 1} />
          ))}
        </div>
      </section>

      {/* ── TRANSPARENCY POLICY ──────────────────────────────────── */}
      <section className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #0E2B40 100%)` }}>
        <div className="absolute inset-0 pointer-events-none opacity-30" style={{
          backgroundImage: `radial-gradient(circle at 20% 30%, rgba(16,185,129,0.25) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(16,185,129,0.15) 0%, transparent 40%)`
        }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="grid lg:grid-cols-12 gap-14 items-start">
            <div className="lg:col-span-5">
              <SectionEyebrow>{tr ? 'Şeffaflık Sözümüz' : 'Transparency Commitment'}</SectionEyebrow>
              <h2 className="font-playfair text-4xl md:text-5xl leading-[1.05] mt-5 text-white">
                {tr ? 'Karanlık nokta yok.' : 'Zero blind spots.'}
              </h2>
              <p className="text-[15px] text-white/70 leading-relaxed mt-6 max-w-md">
                {tr ? 'SummitFlow, zirvenin ruhuna uygun olarak tedarikçi ekosisteminde tam şeffaflığı kural haline getirir. Aşağıdaki taahhütler tüm tedarikçilerimiz için bağlayıcıdır.'
                    : 'In line with the spirit of the summit, SummitFlow makes full transparency a rule across the supplier ecosystem. The commitments below are binding for every supplier we host.'}
              </p>
            </div>
            <div className="lg:col-span-7">
              <PromiseRow icon={Eye}
                title={tr ? 'Açık Komisyon Yapısı' : 'Open Commission Structure'}
                copy={tr ? 'Her sipariş kalemi için kesilen komisyon faturanızda kalem kalem gösterilir. Sözleşmeli oranın dışında ek ücret talep edilmez.'
                        : 'The commission deducted on every line item is itemized on your invoice. We never charge anything beyond the contracted rate.'} />
              <PromiseRow icon={FileText}
                title={tr ? 'Erişilebilir Sözleşme & Politikalar' : 'Public Contract & Policies'}
                copy={tr ? 'Tedarikçi sözleşmesi, fiyatlandırma kuralları ve iade politikaları kayıt sırasında ve panelinizden istediğiniz an erişilebilir durumdadır.'
                        : 'The supplier contract, pricing rules and refund policies are accessible during signup and from your dashboard at any time.'} />
              <PromiseRow icon={ScanSearch}
                title={tr ? 'Denetim İzi' : 'Audit Trail'}
                copy={tr ? 'Tüm sipariş, ödeme ve sözleşme aksiyonları zaman damgalı kayıt altındadır. Hem yöneticiler hem tedarikçiler aksiyon geçmişini görebilir.'
                        : 'Every order, payment and contract action is time-stamped and logged. Both admins and suppliers can inspect the action history.'} />
              <PromiseRow icon={Lock}
                title={tr ? 'KVKK & GDPR Uyumlu Veri' : 'KVKK & GDPR-Compliant Data'}
                copy={tr ? 'Kişisel ve şirket verileri en az hak ilkesiyle saklanır; üçüncü taraflarla pazarlama amaçlı paylaşılmaz.'
                        : 'Personal and company data is stored under the principle of least privilege and is never shared with third parties for marketing.'} />
              <PromiseRow icon={Handshake}
                title={tr ? 'Çift Yönlü Değerlendirme' : 'Two-Way Rating'}
                copy={tr ? 'Alıcılar tedarikçileri puanlar; tedarikçiler de problemli alıcıları işaretleyebilir. Skor algoritması herkese açıktır.'
                        : 'Buyers rate suppliers; suppliers can flag problematic buyers. The scoring algorithm is publicly documented.'} />
            </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <div className="relative bg-white border border-[rgba(10,58,90,0.10)] rounded-3xl p-10 md:p-16 overflow-hidden">
          <div className="absolute -top-32 -right-32 w-96 h-96 bg-emerald-100 rounded-full blur-3xl opacity-60 pointer-events-none" />
          <div className="relative grid md:grid-cols-2 gap-10 items-center">
            <div>
              <SectionEyebrow>{tr ? 'Hemen Başlayın' : 'Start Now'}</SectionEyebrow>
              <h3 className="font-playfair text-3xl md:text-4xl leading-tight mt-4" style={{ color: NAVY }}>
                {tr ? 'Yer sınırlı — başvurunuz 48 saatte değerlendirilir.'
                    : 'Spaces are limited — applications are reviewed within 48 hours.'}
              </h3>
            </div>
            <div className="flex flex-col gap-3">
              <Link to="/tedarikci-kayit" data-testid="supplier-landing-cta-signup"
                className="group inline-flex items-center justify-between gap-3 bg-emerald-600 hover:bg-emerald-700 text-white px-7 py-5 rounded-2xl font-semibold transition-colors">
                <span className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center text-sm font-bold">↗</span>
                  {tr ? 'Tedarikçi Olarak Kayıt Ol' : 'Sign Up as Supplier'}
                </span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/giris?tab=supplier&only=1" data-testid="supplier-landing-cta-signin"
                className="group inline-flex items-center justify-between gap-3 border-2 border-[rgba(10,58,90,0.12)] hover:border-emerald-500 hover:text-emerald-700 px-7 py-5 rounded-2xl font-semibold transition-colors" style={{ color: NAVY }}>
                <span className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center text-sm font-bold text-emerald-700">→</span>
                  {tr ? 'Mevcut Hesabıma Giriş Yap' : 'Sign In to Existing Account'}
                </span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a href="mailto:tedarikci@summitflow.net" data-testid="supplier-landing-contact"
                className="inline-flex items-center gap-2 text-[13px] text-[#5A6B7D] hover:text-emerald-700 mt-2 transition-colors">
                <Mail className="w-3.5 h-3.5" />
                {tr ? 'Sorularınız için tedarikci@summitflow.net' : 'Questions? tedarikci@summitflow.net'}
              </a>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[rgba(10,58,90,0.08)] py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-col md:flex-row gap-3 items-center justify-between text-[12px] text-[#5A6B7D]">
          <p>© 2026 SummitFlow Platform · {tr ? 'Tüm hakları saklıdır.' : 'All rights reserved.'}</p>
          <div className="flex items-center gap-6">
            <Link to="/" className="hover:text-emerald-700 transition-colors">{tr ? 'Ana Sayfa' : 'Home'}</Link>
            <Link to="/tedarikci-kayit" className="hover:text-emerald-700 transition-colors">{tr ? 'Başvuru' : 'Apply'}</Link>
            <Link to="/giris?tab=supplier&only=1" className="hover:text-emerald-700 transition-colors">{tr ? 'Giriş' : 'Sign in'}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
